import type { QueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import { assertPrivateSession, getPrivateSessionState } from "@/lib/api/private-session";
import type { SellerSession } from "@/lib/api/types";
import { firebaseSessionAdapter } from "@/lib/firebase/auth";
import { queryKeys } from "@/shared/queries";

const credentials = new WeakMap<QueryClient, { generation: number; scope: string; issuedAt: number }>();
const REFRESH_MS = 50 * 60_000;
const refreshAfterInspection = new WeakSet<QueryClient>();
export const takeSessionCredentialRefresh = (client: QueryClient): boolean => refreshAfterInspection.delete(client);

function authorizationScope({ seller, mustChangePassword }: SellerSession): string {
  return JSON.stringify([seller.id, seller.storeId, seller.storeSlug, seller.storeKind,
    "storeAccess" in seller ? seller.storeAccess : undefined, mustChangePassword,
    [...(seller.permissions ?? [])].sort(), [...(seller.kitchenStationIds ?? [])].sort()]);
}

/** Session inspection stays fresh; only the optional Firebase credential is reused. */
export async function readSession(client: QueryClient, signal: AbortSignal): Promise<SellerSession> {
  const generation = getPrivateSessionState().generation;
  const previous = client.getQueryData<SellerSession>(queryKeys.session);
  const credential = credentials.get(client);
  const age = credential ? Date.now() - credential.issuedAt : Infinity;
  const reuse = Boolean(previous?.firebaseToken && credential?.generation === generation &&
    credential.scope === authorizationScope(previous) && age >= 0 && age < REFRESH_MS);
  const request = new AbortController();
  const abort = () => request.abort();
  signal.addEventListener("abort", abort, { once: true });
  if (signal.aborted) abort();
  const deadline = setTimeout(abort, 10_000);
  const read = (mint: boolean) => apiFetch<SellerSession>(
    `${endpoints.seller.me}${mint ? "?includeFirebaseToken=true" : ""}`,
    { cache: "no-store", signal: request.signal },
  );
  try {
    let payload = await read(!reuse);
    assertPrivateSession(generation);
    if (request.signal.aborted) throw new DOMException("Session read aborted", "AbortError");
    // Authorization changes revoke the SDK lease even when the cached custom
    // token has aged out and this request already attempted a fresh mint.
    if (previous && authorizationScope(previous) !== authorizationScope(payload)) {
      void firebaseSessionAdapter.signOut().catch(() => undefined);
    }
    if (reuse && authorizationScope(payload) === credential!.scope) {
      payload = { ...payload, firebaseToken: previous!.firebaseToken };
    } else {
      credentials.delete(client);
      if (reuse) {
        // Publish lost/changed access before optional credential recovery can fail.
        refreshAfterInspection.add(client);
        payload = { ...payload, firebaseToken: null };
      }
      assertPrivateSession(generation);
      if (request.signal.aborted) throw new DOMException("Session read aborted", "AbortError");
      if (payload.firebaseToken) credentials.set(client, {
        generation, scope: authorizationScope(payload), issuedAt: Date.now(),
      });
    }
    assertPrivateSession(generation);
    if (request.signal.aborted) throw new DOMException("Session read aborted", "AbortError");
    return payload;
  } finally {
    clearTimeout(deadline);
    signal.removeEventListener("abort", abort);
  }
}
